import { X, Loader2, CheckCircle, AlertCircle, Image, Video } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import type { Asset } from '@/types'

export interface UploadItem {
  id: string
  file: File
  status: 'pending' | 'uploading' | 'success' | 'error'
  progress: number
  error?: string
  resultAsset?: Asset
}

interface UploadProgressListProps {
  items: UploadItem[]
  onRemove: (id: string) => void
  onClearCompleted: () => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(file: File) {
  if (file.type.startsWith('video/')) {
    return <Video className="w-4 h-4 text-purple-500" />
  }
  return <Image className="w-4 h-4 text-blue-500" />
}

function UploadItemRow({
  item,
  onRemove,
}: {
  item: UploadItem
  onRemove: (id: string) => void
}) {
  const canRemove = item.status === 'success' || item.status === 'error'

  return (
    <div className="p-3 hover:bg-gray-50">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {item.status === 'uploading' ? (
            <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
          ) : item.status === 'success' ? (
            <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
          ) : item.status === 'error' ? (
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          ) : (
            getFileIcon(item.file)
          )}
          <span className="text-sm truncate" title={item.file.name}>
            {item.file.name}
          </span>
        </div>
        {canRemove && (
          <button
            onClick={() => onRemove(item.id)}
            className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">{formatFileSize(item.file.size)}</span>
        {item.status === 'uploading' && (
          <>
            <div className="flex-1">
              <Progress value={item.progress} className="h-1" />
            </div>
            <span className="text-xs text-gray-500 w-8">{item.progress}%</span>
          </>
        )}
        {item.status === 'error' && (
          <span className="text-xs text-red-600">{item.error || 'Upload failed'}</span>
        )}
        {item.status === 'success' && (
          <span className="text-xs text-green-600">Uploaded</span>
        )}
        {item.status === 'pending' && (
          <span className="text-xs text-gray-500">Waiting...</span>
        )}
      </div>
    </div>
  )
}

export function UploadProgressList({
  items,
  onRemove,
  onClearCompleted,
}: UploadProgressListProps) {
  if (items.length === 0) {
    return null
  }

  const uploadingCount = items.filter((i) => i.status === 'uploading').length
  const pendingCount = items.filter((i) => i.status === 'pending').length
  const completedCount = items.filter((i) => i.status === 'success').length
  const errorCount = items.filter((i) => i.status === 'error').length
  const inProgressCount = uploadingCount + pendingCount

  return (
    <div className="fixed bottom-6 right-6 z-40 w-80 max-h-[60vh] overflow-hidden bg-white rounded-xl shadow-xl border border-gray-200/60 flex flex-col">
      <div className="p-3 border-b bg-gray-50 flex items-center justify-between flex-shrink-0">
        <span className="font-medium text-sm">
          {inProgressCount > 0
            ? `Uploading ${inProgressCount} file${inProgressCount > 1 ? 's' : ''}`
            : completedCount > 0 && errorCount === 0
              ? 'Upload complete'
              : errorCount > 0
                ? `${errorCount} failed`
                : 'Upload'}
        </span>
        {completedCount > 0 && (
          <button
            onClick={onClearCompleted}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear completed
          </button>
        )}
      </div>
      <div className="divide-y overflow-y-auto flex-1">
        {items.map((item) => (
          <UploadItemRow key={item.id} item={item} onRemove={onRemove} />
        ))}
      </div>
    </div>
  )
}
